import type { Schemas } from "./api"
import { stringifyExact } from "./lossless"
import type { WhyResponse, WhyStep } from "./why"

type FieldEvidence = Schemas["FieldEvidence"]

/**
 * Where one property's current value came from, from recorded answers only:
 * - `source`: mapped external evidence for the exact object version shown;
 * - `hidden`: the cell withheld this history (`Why.masked`), never guessed from roles;
 * - `action`: the subject's own recorded fact step that wrote this property with exactly the
 *   current value, bound to the action call committed in the same commit;
 * - `none`: nothing recorded attributes it (or the read budget ended first).
 */
export type FieldAttribution =
  | { readonly kind: "source"; readonly evidence: FieldEvidence }
  | { readonly kind: "hidden" }
  | { readonly kind: "action"; readonly step: WhyStep; readonly recordedAt: string | null }
  | { readonly kind: "none" }

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

/** The subject identity a fact step carries (`{ objectType, key }`), as Why names it (`object:type/key`). */
function stepSubject(step: WhyStep): string | null {
  const key = record(step.key)
  return key && typeof key.objectType === "string" && typeof key.key === "string" ? `object:${key.objectType}/${key.key}` : null
}

/** The value a fact step wrote for this property, when it wrote one. */
function written(step: WhyStep, property: string): { value: unknown } | null {
  const values = record((step as Record<string, unknown>).values)
  if (values && property in values) return { value: values[property] }
  if (step.property === property && "value" in step) return { value: (step as Record<string, unknown>).value }
  if (step.lifecycle === property && typeof step.to === "string") return { value: step.to }
  return null
}

/** The version the Why root explains (`fact:type/key@v3` → 3). */
function rootVersion(why: WhyResponse): number | null {
  const m = /@v(\d+)$/.exec(why.root)
  return m ? Number(m[1]) : null
}

/** Whether the cell withheld this whole history (no nodes or steps, explicit masked properties). */
export function historyWithheld(why: WhyResponse | undefined): boolean {
  return Boolean(why?.masked && Object.keys(why.masked).length > 0 && why.nodes.length === 0 && why.steps.length === 0)
}

export function attributeField(
  property: string,
  value: unknown,
  version: number,
  why: WhyResponse | undefined,
  provenance: Schemas["ProvenanceResponse"] | undefined,
): FieldAttribution {
  if (provenance && provenance.version === version) {
    const evidence = provenance.properties.find((p) => p.property === property)?.evidence.at(-1)
    if (evidence) return { kind: "source", evidence }
  }
  if (!why) return { kind: "none" }
  if (historyWithheld(why) || (why.masked && property in why.masked)) return { kind: "hidden" }
  // Only a Why that explains exactly the version shown can attribute its values.
  if (rootVersion(why) !== version) return { kind: "none" }
  const exact = stringifyExact(value)
  const times = new Map(why.nodes.map((n) => [n.id, n.recordedAt]))
  // Newest first: the subject's latest recorded write of this property is the one shown.
  for (const step of [...why.steps].reverse()) {
    if (step.kind === "action") continue
    // An observed write (nested event or fact) may have changed any property; without decoding it,
    // nothing older can be credited for the value shown.
    if (step.event !== undefined || step.fact !== undefined || /observ/i.test(step.kind)) return { kind: "none" }
    if (stepSubject(step) !== why.subject) continue
    const write = written(step, property)
    if (!write) continue
    if (stringifyExact(write.value) !== exact) return { kind: "none" }
    const call = why.steps.find((s) => s.kind === "action" && s.commit === step.commit)
    return call ? { kind: "action", step: call, recordedAt: times.get(step.commit) ?? null } : { kind: "none" }
  }
  return { kind: "none" }
}
